import React, { useState, useMemo } from 'react';
import { X, ShoppingCart, CreditCard, MapPin, Package, Check, AlertCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Post } from '../types';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';
import { paymentsService } from '../services/paymentsService';
import { PaymentForm } from './PaymentForm';

interface PurchaseModalProps {
  post: Post;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({ post, isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const { select, locale } = useLanguage();
  const [step, setStep] = useState<'details' | 'delivery' | 'summary' | 'payment' | 'success'>('details');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoiceData, setInvoiceData] = useState<{ pdfBase64?: string } | null>(null);
  const [currentOrder, setCurrentOrder] = useState<Record<string, unknown> | null>(null);

  // Payment state
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<unknown> | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const [purchaseData, setPurchaseData] = useState({
    buyer_name: user?.full_name || '',
    buyer_email: user?.email || '',
    delivery_method: 'digital' as 'digital' | 'physical' | 'both',
    delivery_address: {
      street: '',
      city: '',
      postal_code: '',
      country: select({ es: 'España', en: 'Spain' }),
      phone: ''
    },
    buyer_notes: ''
  });

  const finalPrice = Number(post.price) || 0;
  
  const elementsOptions = useMemo(() => {
    if (!clientSecret) return undefined;
    return {
      clientSecret,
      appearance: { theme: 'stripe' as const }
    };
  }, [clientSecret]);

  if (!isOpen) return null;

  const commissionRate = 10;
  const commissionAmount = finalPrice * (commissionRate / 100);
  const artistEarnings = finalPrice - commissionAmount;

  const handleSubmitDetails = (e: React.FormEvent) => {
    e.preventDefault();
    if (!purchaseData.buyer_name || !purchaseData.buyer_email) {
      setError(select({ es: 'Por favor completa todos los campos obligatorios', en: 'Please complete all required fields' }));
      return;
    }
    setError('');
    setStep('delivery');
  };

  const handleSubmitDelivery = (e: React.FormEvent) => {
    e.preventDefault();
    // Actualmente solo se permite entrega digital
    setError('');
    setStep('summary');
  };

  const handleProceedToPayment = async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Create Pending Order (if not exists)
      let order = currentOrder;
      if (!order) {
        // Validate seller_id to prevent 23502 error
        const sellerId = post.author_id || post.author?.id;
        if (!sellerId) {
          throw new Error(select({ es: 'Error: No se pudo identificar al vendedor', en: 'Error: Could not identify seller' }));
        }

        const { data, error: orderError } = await supabase
          .from('orders')
          .insert({
            seller_id: sellerId,
            buyer_id: user?.id || null,
            buyer_email: purchaseData.buyer_email,
            buyer_name: purchaseData.buyer_name,
            post_id: post.id,
            original_price: finalPrice,
            final_price: finalPrice,
            commission_rate: commissionRate,
            commission_amount: commissionAmount,
            artist_earnings: artistEarnings,
            delivery_method: 'digital',
            delivery_address: null,
            status: 'pending',
            payment_status: 'pending',
            buyer_notes: purchaseData.buyer_notes || null,
          })
          .select()
          .single();

        if (orderError) throw orderError;
        order = data;
        setCurrentOrder(order);
      }

      // 2. Get Stripe Key
      const key = await paymentsService.getStripePublishableKey();
      if (!key) throw new Error('Stripe configuration missing');
      setStripePromise(loadStripe(key));

      // 3. Create Payment Intent
      const { client_secret } = await paymentsService.createPaymentIntent(
        finalPrice,
        'eur',
        {
          post_id: post.id,
          buyer_id: user?.id,
          buyer_email: purchaseData.buyer_email,
          order_id: order.id, // Link to order
        }
      );
      setClientSecret(client_secret);
      setStep('payment');
    } catch (err) {
      console.error('Payment initialization error:', err);
      setError(err instanceof Error ? err.message : select({ es: 'Error al iniciar el pago', en: 'Error initializing payment' }));
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizePurchase = async () => {
    setLoading(true);
    setError('');

    try {
      if (!currentOrder) throw new Error('No active order found');

      // Update Order Status via Secure Edge Function (bypasses RLS)
      // Notifications are now handled in the Edge Function 'payments' to bypass RLS issues
      const order = await paymentsService.confirmPayment(currentOrder.id);

      // Notify Seller and Buyer (Moved to Backend)
      /* 
      try {
        const sellerId = post.author_id || post.author?.id;
        
        // Notify Seller
        if (sellerId) {
          await notificationsService.createSaleNotification(
            sellerId,
            purchaseData.buyer_name,
            post.title,
            finalPrice,
            post.id,
            user?.id
          );
        }

        // Notify Buyer
        if (user?.id) {
           const sellerName = post.author?.username || post.author?.full_name || 'Artista';
           await notificationsService.createPurchaseNotification(
             user.id,
             sellerName,
             post.title,
             finalPrice,
             post.id,
             sellerId
           );
        }
      } catch (notifyError) {
        console.error('Notification failed:', notifyError);
      }
      */

      try {
        // Generate Buyer Receipt (Client-side)
        const receiptData = await paymentsService.generateBuyerReceipt(order, post);
        setInvoiceData(receiptData);

        // Trigger Commission Invoice (Backend) - Invoice for the 10% commission to the Artist
        await paymentsService.generateCommissionInvoice(order.id);
      } catch (holdedError) {
        console.error('Invoice/Receipt generation failed:', holdedError);
      }

      setStep('success');
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 3000);

    } catch (err) {
      console.error('Finalize Purchase Error:', err);
      const errorMessage = err instanceof Error ? err.message : select({ es: 'Error al procesar el pedido', en: 'Error processing the order' });
      setError(errorMessage);
    } finally {
      setLoading(false);
      setIsProcessingPayment(false); // Ensure PaymentForm stops loading
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 md:p-4 z-[9999]">
      <div className="bg-white w-full h-full md:rounded-2xl md:max-w-2xl md:w-full md:max-h-[90vh] md:h-auto overflow-y-auto modal-detail">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ShoppingCart className="text-green-600" size={24} />
            <h2 className="text-2xl font-bold text-gray-900">
              {select({ es: 'Comprar obra', en: 'Purchase artwork' })}
            </h2>
          </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors touch-target"
            >
              <X size={20} />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8">
            {[
              { key: 'details', label: select({ es: 'Datos', en: 'Details' }), icon: CreditCard },
              { key: 'delivery', label: select({ es: 'Entrega', en: 'Delivery' }), icon: Package },
              { key: 'summary', label: select({ es: 'Resumen', en: 'Summary' }), icon: Check },
            ].map((stepItem, index) => {
              const Icon = stepItem.icon;
              const isActive = step === stepItem.key;
              const isCompleted = ['details', 'delivery', 'summary'].indexOf(step) > index;
              
              return (
                <div key={stepItem.key} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    isActive 
                      ? 'bg-green-600 text-white' 
                      : isCompleted 
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-400'
                  }`}>
                    <Icon size={16} />
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    isActive ? 'text-green-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {stepItem.label}
                  </span>
                  {index < 2 && (
                    <div className={`w-12 h-0.5 mx-4 ${
                      isCompleted ? 'bg-green-200' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Step 1: Details */}
          {step === 'details' && (
            <form onSubmit={handleSubmitDetails} className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-4">
                  {post.media?.[0] && (
                    <img
                      src={post.media[0].url}
                      alt={post.title}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-900">{post.title}</h3>
                    <p className="text-gray-600 text-sm">Por @{post.author.username}</p>
                    <p className="text-2xl font-bold text-green-600 mt-2">{formatPrice(finalPrice)}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    required
                    value={purchaseData.buyer_name}
                    onChange={(e) => setPurchaseData(prev => ({...prev, buyer_name: e.target.value}))}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                    placeholder="Tu nombre completo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={purchaseData.buyer_email}
                    onChange={(e) => setPurchaseData(prev => ({...prev, buyer_email: e.target.value}))}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {select({ es: 'Notas adicionales (opcional)', en: 'Additional notes (optional)' })}
                </label>
                <textarea
                  rows={3}
                  value={purchaseData.buyer_notes}
                  onChange={(e) => setPurchaseData(prev => ({...prev, buyer_notes: e.target.value}))}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-base"
                  placeholder={select({
                    es: 'Alguna especificación o mensaje para el artista...',
                    en: 'Any specifics or message for the artist...',
                  })}
                />
              </div>

              <button
                type="submit"
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium touch-target text-base"
              >
                Continuar con entrega
              </button>
            </form>
          )}

          {/* Step 2: Delivery */}
          {step === 'delivery' && (
            <form onSubmit={handleSubmitDelivery} className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Método de entrega</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="delivery"
                      value="digital"
                      checked={purchaseData.delivery_method === 'digital'}
                      onChange={(e) => setPurchaseData(prev => ({...prev, delivery_method: e.target.value as 'digital' | 'physical' | 'both'}))}
                      className="text-green-600 focus:ring-green-500"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Entrega digital</p>
                      <p className="text-sm text-gray-600">Descarga inmediata tras el pago</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg opacity-60 cursor-not-allowed">
                    <input
                      type="radio"
                      name="delivery"
                      value="physical"
                      checked={false}
                      disabled
                      aria-disabled="true"
                      className="text-green-600 focus:ring-green-500"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Entrega física</p>
                      <p className="text-sm text-gray-600">Temporalmente no disponible. Solo entrega digital.</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg opacity-60 cursor-not-allowed">
                    <input
                      type="radio"
                      name="delivery"
                      value="both"
                      checked={false}
                      disabled
                      aria-disabled="true"
                      className="text-green-600 focus:ring-green-500"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Ambos</p>
                      <p className="text-sm text-gray-600">Temporalmente no disponible. Solo entrega digital.</p>
                    </div>
                  </label>
                </div>
              </div>

              {(purchaseData.delivery_method === 'physical' || purchaseData.delivery_method === 'both') && (
                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="text-blue-600" size={20} />
                    <h4 className="font-medium text-blue-900">Dirección de entrega</h4>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {select({ es: 'Dirección *', en: 'Address *' })}
                      </label>
                      <input
                        type="text"
                        required
                        value={purchaseData.delivery_address.street}
                        onChange={(e) => setPurchaseData(prev => ({
                          ...prev, 
                          delivery_address: {...prev.delivery_address, street: e.target.value}
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder={select({ es: 'Calle y número', en: 'Street and number' })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {select({ es: 'Ciudad *', en: 'City *' })}
                      </label>
                      <input
                        type="text"
                        required
                        value={purchaseData.delivery_address.city}
                        onChange={(e) => setPurchaseData(prev => ({
                          ...prev, 
                          delivery_address: {...prev.delivery_address, city: e.target.value}
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder={select({ es: 'Tu ciudad', en: 'Your city' })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {select({ es: 'Código postal *', en: 'ZIP / Postal code *' })}
                      </label>
                      <input
                        type="text"
                        required
                        value={purchaseData.delivery_address.postal_code}
                        onChange={(e) => setPurchaseData(prev => ({
                          ...prev, 
                          delivery_address: {...prev.delivery_address, postal_code: e.target.value}
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder={select({ es: '28001', en: '10001' })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {select({ es: 'País', en: 'Country' })}
                      </label>
                      <select
                        value={purchaseData.delivery_address.country}
                        onChange={(e) => setPurchaseData(prev => ({
                          ...prev, 
                          delivery_address: {...prev.delivery_address, country: e.target.value}
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        {['España', 'Francia', 'Portugal', 'Italia', 'Alemania'].map((country) => (
                          <option key={country} value={country}>
                            {select({ es: country, en: country })}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {select({ es: 'Teléfono', en: 'Phone' })}
                      </label>
                      <input
                        type="tel"
                        value={purchaseData.delivery_address.phone}
                        onChange={(e) => setPurchaseData(prev => ({
                          ...prev, 
                          delivery_address: {...prev.delivery_address, phone: e.target.value}
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder={select({ es: '+34 600 000 000', en: '+1 555 000 0000' })}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep('details')}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {select({ es: 'Atrás', en: 'Back' })}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium touch-target text-base"
                >
                  {select({ es: 'Revisar pedido', en: 'Review order' })}
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Summary */}
          {step === 'summary' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {select({ es: 'Resumen del pedido', en: 'Order summary' })}
              </h3>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-start gap-4 mb-4">
                  {post.media?.[0] && (
                    <img
                      src={post.media[0].url}
                      alt={post.title}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{post.title}</h4>
                    <p className="text-gray-600 text-sm">Por @{post.author.username}</p>
                    <p className="text-sm text-gray-500 mt-1">{post.description.substring(0, 100)}...</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">{formatPrice(finalPrice)}</p>
                  </div>
                </div>
              </div>

              {/* Buyer Info */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">
                  {select({ es: 'Datos del comprador', en: 'Buyer information' })}
                </h4>
                <div className="space-y-2 text-sm">
                  <p>
                    <span className="text-gray-600">{select({ es: 'Nombre:', en: 'Name:' })}</span> {purchaseData.buyer_name}
                  </p>
                  <p>
                    <span className="text-gray-600">Email:</span> {purchaseData.buyer_email}
                  </p>
                  <p>
                    <span className="text-gray-600">{select({ es: 'Entrega:', en: 'Delivery:' })}</span>{' '}
                    {purchaseData.delivery_method === 'digital'
                      ? select({ es: 'Digital', en: 'Digital' })
                      : purchaseData.delivery_method === 'physical'
                        ? select({ es: 'Física', en: 'Physical' })
                        : select({ es: 'Digital + Física', en: 'Digital + Physical' })}
                  </p>
                  {(purchaseData.delivery_method === 'physical' || purchaseData.delivery_method === 'both') && (
                    <div className="bg-blue-50 p-3 rounded mt-2">
                      <p className="text-gray-700">
                        {purchaseData.delivery_address.street}<br />
                        {purchaseData.delivery_address.city}, {purchaseData.delivery_address.postal_code}<br />
                        {purchaseData.delivery_address.country}
                        {purchaseData.delivery_address.phone && (
                          <>
                            <br />
                            {select({ es: 'Tel:', en: 'Phone:' })} {purchaseData.delivery_address.phone}
                          </>
                        )}
                      </p>
                    </div>
                  )}
                  {purchaseData.buyer_notes && (
                    <div className="bg-yellow-50 p-3 rounded mt-2">
                      <p className="text-gray-700">
                        <span className="font-medium">{select({ es: 'Notas:', en: 'Notes:' })}</span> {purchaseData.buyer_notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>



              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-700">
                  {select({
                    es: '🛡️ Tu compra está protegida. El artista recibirá una notificación de tu pedido y podrás hacer seguimiento del estado desde tu perfil.',
                    en: '🛡️ Your purchase is protected. The artist will be notified of your order and you can track its status from your profile.',
                  })}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('delivery')}
                  disabled={loading}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 touch-target text-base"
                >
                  {select({ es: 'Atrás', en: 'Back' })}
                </button>
               <button
                 onClick={handleProceedToPayment}
                 disabled={loading}
                 className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2 touch-target text-base"
               >
                 {loading ? (
                   <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      {select({ es: 'Procesando...', en: 'Processing...' })}
                    </>
                  ) : (
                    select({ es: 'Ir al pago', en: 'Proceed to Payment' })
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3.5: Payment */}
          {step === 'payment' && clientSecret && stripePromise && elementsOptions && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {select({ es: 'Pago seguro', en: 'Secure Payment' })}
              </h3>
              
              <Elements 
                stripe={stripePromise} 
                options={elementsOptions}
              >
                <PaymentForm 
                  amount={finalPrice} 
                  onSuccess={(paymentIntentId) => handleFinalizePurchase(paymentIntentId)}
                  onError={(err) => setError(err)}
                  isProcessing={isProcessingPayment}
                  setIsProcessing={setIsProcessingPayment}
                />
              </Elements>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => setStep('summary')}
                  disabled={isProcessingPayment}
                  className="text-gray-500 hover:text-gray-700 text-sm font-medium"
                >
                  {select({ es: 'Cancelar y volver', en: 'Cancel and return' })}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="text-white" size={40} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {select({ es: '¡Pedido realizado con éxito!', en: 'Order placed successfully!' })}
              </h3>
              <p className="text-gray-600 mb-6">
                {select({
                  es: 'Tu pedido ha sido enviado al artista. Recibirás una confirmación por email y podrás hacer seguimiento desde tu perfil.',
                  en: 'Your order has been sent to the artist. You will receive a confirmation by email and can track it from your profile.',
                })}
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-700">
                  {select({
                    es: 'El artista ha sido notificado y procesará tu pedido. Te mantendremos informado del estado del envío y la entrega.',
                    en: 'The artist has been notified and will process your order. We will keep you updated on shipping and delivery.',
                  })}
                </p>
              </div>
              
              {invoiceData?.pdfBase64 && (
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = `data:application/pdf;base64,${invoiceData.pdfBase64}`;
                    link.download = `comprobante-${post.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="block w-full mb-4 px-6 py-3 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors font-medium touch-target text-base flex items-center justify-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                  {select({ es: 'Descargar Comprobante', en: 'Download Receipt' })}
                </button>
              )}

              <button
                onClick={() => {
                  onSuccess();
                  onClose();
                }}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium touch-target text-base"
              >
                {select({ es: 'Entendido', en: 'Got it' })}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseModal;
